import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    StartTimer,
    StopTimer,
    GetActiveTimer,
    GetTimeEntries,
    GetTimeSummary,
    UpdateTimeEntry,
    DeleteTimeEntry,
    CreateTimesheet,
    GetTimesheets,
    GetTimesheetById,
    SubmitTimesheet,
    ApproveTimesheet,
    RejectTimesheet
} from "helpers/api_method";

// Time Tracking Thunks
export const startTimer = createAsyncThunk(
    "timeTracking/startTimer",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await StartTimer(data);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to start timer' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to start timer' });
        }
    }
);

export const stopTimer = createAsyncThunk(
    "timeTracking/stopTimer",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await StopTimer(data);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to stop timer' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to stop timer' });
        }
    }
);

export const getActiveTimer = createAsyncThunk(
    "timeTracking/getActiveTimer",
    async (_, { rejectWithValue }) => {
        try {
            const response = await GetActiveTimer();
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to get active timer' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to get active timer' });
        }
    }
);

export const getTimeEntries = createAsyncThunk(
    "timeTracking/getTimeEntries",
    async (params: any = undefined, { rejectWithValue }: any) => {
        try {
            const response = await GetTimeEntries(params);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to get time entries' });
            }
            return payload?.data ?? [];
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to get time entries' });
        }
    }
);

export const getTimeSummary = createAsyncThunk(
    "timeTracking/getTimeSummary",
    async (params: any = undefined, { rejectWithValue }: any) => {
        try {
            const response = await GetTimeSummary(params);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to get time summary' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to get time summary' });
        }
    }
);

export const updateTimeEntry = createAsyncThunk(
    "timeTracking/updateTimeEntry",
    async ({ timeTrackingId, data }: { timeTrackingId: string, data: any }, { rejectWithValue }) => {
        try {
            const response = await UpdateTimeEntry(timeTrackingId, data);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to update time entry' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to update time entry' });
        }
    }
);

export const deleteTimeEntry = createAsyncThunk(
    "timeTracking/deleteTimeEntry",
    async (timeTrackingId: string, { rejectWithValue }) => {
        try {
            const response = await DeleteTimeEntry(timeTrackingId);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to delete time entry' });
            }
            return timeTrackingId;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to delete time entry' });
        }
    }
);

// Timesheet Thunks
export const createTimesheet = createAsyncThunk(
    "timesheet/createTimesheet",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await CreateTimesheet(data);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to create timesheet' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to create timesheet' });
        }
    }
);

export const getTimesheets = createAsyncThunk(
    "timesheet/getTimesheets",
    async (params: any = undefined, { rejectWithValue }: any) => {
        try {
            const response = await GetTimesheets(params);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to get timesheets' });
            }
            return payload?.data ?? [];
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to get timesheets' });
        }
    }
);

export const getTimesheetById = createAsyncThunk(
    "timesheet/getTimesheetById",
    async (timesheetId: string, { rejectWithValue }) => {
        try {
            const response = await GetTimesheetById(timesheetId);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to get timesheet' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to get timesheet' });
        }
    }
);

export const submitTimesheet = createAsyncThunk(
    "timesheet/submitTimesheet",
    async (timesheetId: string, { rejectWithValue }) => {
        try {
            const response = await SubmitTimesheet(timesheetId);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to submit timesheet' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to submit timesheet' });
        }
    }
);

export const approveTimesheet = createAsyncThunk(
    "timesheet/approveTimesheet",
    async ({ timesheetId, data }: { timesheetId: string, data: any }, { rejectWithValue }) => {
        try {
            const response = await ApproveTimesheet(timesheetId, data);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to approve timesheet' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to approve timesheet' });
        }
    }
);

export const rejectTimesheet = createAsyncThunk(
    "timesheet/rejectTimesheet",
    async ({ timesheetId, data }: { timesheetId: string, data: any }, { rejectWithValue }) => {
        try {
            const response = await RejectTimesheet(timesheetId, data);
            const payload = response as any;
            const hasBooleanStatus = typeof payload?.status === 'boolean';
            if (hasBooleanStatus && payload.status === false) {
                return rejectWithValue({ error: payload.message || 'Failed to reject timesheet' });
            }
            return payload?.data ?? null;
        } catch (error: any) {
            return rejectWithValue({ error: error.message || 'Failed to reject timesheet' });
        }
    }
);

